import React from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Sparkles, CheckCircle2 } from 'lucide-react';
import { db } from '../../firebase';

function formatDate(ts) {
  const d = ts?.toDate?.();
  if (!d) return '';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function ReleaseNotesModal({ notes, unseenNotes, onClose, userProfile, setUserProfile }) {
  const hasUnseen = unseenNotes.length > 0;

  const handleMarkSeen = async () => {
    try {
      await updateDoc(doc(db, 'users', userProfile.uid), {
        lastSeenReleaseAt: serverTimestamp(),
      });
      setUserProfile(prev => ({
        ...prev,
        lastSeenReleaseAt: { toDate: () => new Date() },
      }));
    } catch {}
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
            <Sparkles size={16} className="text-blue-600" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">What's new</h2>
            {hasUnseen && (
              <p className="text-xs text-blue-600 font-medium">
                {unseenNotes.length} new update{unseenNotes.length !== 1 ? 's' : ''} since your last visit
              </p>
            )}
          </div>
        </div>

        {/* Notes list */}
        <div className="overflow-y-auto px-6 py-4 space-y-6 flex-1">
          {notes.length === 0 && (
            <p className="text-sm text-gray-600 text-center py-6">No updates yet — check back soon.</p>
          )}
          {notes.map(note => {
            const isUnseen = unseenNotes.some(u => u.id === note.id);
            return (
              <div key={note.id}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{note.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{formatDate(note.publishedAt)}</p>
                  </div>
                  {isUnseen && (
                    <span className="shrink-0 text-[10px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full mt-0.5">
                      NEW
                    </span>
                  )}
                </div>
                <ul className="space-y-1.5 ml-1">
                  {(note.items || []).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <span className="text-blue-500 mt-1 shrink-0" aria-hidden="true">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 shrink-0 flex justify-end gap-3">
          {!hasUnseen && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          )}
          {hasUnseen && (
            <button
              onClick={handleMarkSeen}
              className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <CheckCircle2 size={15} aria-hidden="true" />
              Got it, thanks!
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
